// initRum: the RUM entry point an SPA calls once at boot (web: boot/app-boot.tsx,
// seller and admin-internal: main.tsx). The admin console does not call it.
//
// Wires Core Web Vitals + the client-error pipeline into batch queues and flushes
// them (POST /api/rum, POST /api/client-errors) when the page is backgrounded or
// unloading, the only moments the browser reliably sends final telemetry. Errors
// also flush on a one-shot timer while the page stays visible. All work is
// best-effort and off the render path. Sampling and transport defaults: see
// RumConfig.

import { sendBatch, type Transport } from "./beacon";
import {
  drainErrors,
  installGlobalErrorHandlers,
  reportPusherHealth,
  setErrorFlushScheduler,
  setErrorsEnabled,
} from "./errors";
import type {
  ClientErrorBatchRequest,
  RumBatchRequest,
  RumConfig,
  RumConnectionType,
  RumVital,
} from "./types";
import { registerVitals } from "./web-vitals";
import { currentEntryPoint } from "../analytics/track";
import { setPusherHealthHandler } from "../pusher/pusher-health";

const RUM_PATH = "/api/rum";
const ERRORS_PATH = "/api/client-errors";
const URL_MAX = 512; // RumBatchRequest.url / ClientErrorBatchRequest.url
const MAX_VITALS = 24; // RumBatchRequest.vitals

let initialized = false;
let vitalsQueue: RumVital[] = [];
let removeErrorHandlers: (() => void) | null = null;
let onVisibility: (() => void) | null = null;
let onPageHide: (() => void) | null = null;
let errorFlushTimer: ReturnType<typeof setTimeout> | null = null;

// How long an enqueued error may wait before it is flushed while the page stays
// visible. One one-shot timer per quiet window — armed by an error, never on a
// schedule — so this is not a polling loop; it exists because a tab mid-incident
// (fetch failures firing) may never hit the visibility/pagehide triggers.
const ERROR_FLUSH_DELAY_MS = 5_000;

// App-level counters (e.g. the api-client's coalesced-request / queue-depth
// counters) ride the vitals batch as extra `RumVital` entries, landing on the
// same `rum_web_vital{app,metric_name}` log-based metric with no new backend
// surface. Suppliers return-and-reset their counters; they are pulled once per
// vitals flush. Registration is independent of initRum so module init order
// doesn't matter (a sampled-out page simply never pulls).
type AppMetricsSupplier = () => RumVital[];
let metricsSuppliers: AppMetricsSupplier[] = [];

/** Register a supplier of app metrics to ride the next vitals flush. */
export function registerAppMetricsSupplier(supplier: AppMetricsSupplier): void {
  metricsSuppliers.push(supplier);
}

const CONNECTION_TYPES: ReadonlySet<string> = new Set<RumConnectionType>([
  "slow-2g",
  "2g",
  "3g",
  "4g",
]);

/**
 * The network class the page loaded on, folded to the server's closed set
 * (`RumConnectionType`): a value the server does not know would 422 the whole batch.
 * Safari and Firefox expose no `navigator.connection`, so they read `unknown`.
 */
function loadConnectionType(): RumConnectionType {
  try {
    const effective = (navigator as Navigator & { connection?: { effectiveType?: string } })
      .connection?.effectiveType;
    return effective !== undefined && CONNECTION_TYPES.has(effective)
      ? (effective as RumConnectionType)
      : "unknown";
  } catch {
    return "unknown";
  }
}

function currentUrl(): string {
  // Path only — origin + pathname, deliberately dropping the query string and
  // fragment. `?debug=1` today (and any future token/PII-bearing param) must
  // never ride a RUM / client-error beacon into a Cloud Logging line.
  if (typeof location === "undefined") return "";
  const url = location.origin + location.pathname;
  return url.length > URL_MAX ? url.slice(0, URL_MAX) : url;
}

/**
 * Initialize Real User Monitoring for this page. Idempotent — a second call is a
 * no-op. See {@link RumConfig} for the transport/sampling defaults.
 */
export function initRum(config: RumConfig): void {
  if (initialized) return;

  const sampleRate = config.sampleRate ?? 1;
  // Decide inclusion once, up front. A sampled-out page installs nothing.
  if (!(Math.random() < sampleRate)) return;
  initialized = true;

  const { app } = config;
  const transport: Transport = config.transport ?? (app === "seller" ? "fetch" : "beacon");
  const endpoint = config.endpoint ?? "";

  setErrorsEnabled(true);
  // Read at boot, not at flush: the vitals describe the load, and a flush can come
  // minutes later on a different network.
  const connectionType = loadConnectionType();

  const flushVitals = (): void => {
    for (const supplier of metricsSuppliers) {
      // A supplier must never break telemetry (doctrine 1.5) — skip it on throw.
      try {
        vitalsQueue.push(...supplier());
      } catch {
        /* best-effort */
      }
    }
    if (vitalsQueue.length === 0) return;
    const batch: RumBatchRequest = {
      app,
      url: currentUrl(),
      vitals: vitalsQueue.slice(0, MAX_VITALS),
      ts: Date.now(),
      // The funnel's referring surface (`?mira_source=`, sticky per session), read at
      // flush because `initTracking` may bind it after `initRum`. Undefined = none named.
      entry_point: currentEntryPoint(),
      connection_type: connectionType,
    };
    vitalsQueue = [];
    sendBatch(RUM_PATH, batch, transport, endpoint);
  };
  const flushErrors = (): void => {
    const errors = drainErrors();
    if (errors.length === 0) return;
    const batch: ClientErrorBatchRequest = {
      app,
      url: currentUrl(),
      errors,
      ts: Date.now(),
    };
    sendBatch(ERRORS_PATH, batch, transport, endpoint);
  };
  const flushAll = (): void => {
    flushVitals();
    flushErrors();
  };

  registerVitals((vital) => {
    // web-vitals emits each metric at most once; the cap is belt-and-suspenders.
    if (vitalsQueue.length < MAX_VITALS) vitalsQueue.push(vital);
  });

  removeErrorHandlers = installGlobalErrorHandlers();
  // Invert the dependency: rum registers its error queue as the pusher manager's
  // health consumer, so pusher/ never imports rum.
  setPusherHealthHandler(reportPusherHealth);
  setErrorFlushScheduler(() => {
    if (errorFlushTimer !== null) return;
    errorFlushTimer = setTimeout(() => {
      errorFlushTimer = null;
      flushErrors();
    }, ERROR_FLUSH_DELAY_MS);
  });

  // Flush when the page is backgrounded (visibilitychange→hidden) or torn down
  // (pagehide). visibilitychange fires on tab switch / minimize on every platform;
  // pagehide covers bfcache eviction + real unloads that skip a hide. No `unload`
  // handler (it disables bfcache and is unreliable on mobile).
  if (typeof document !== "undefined") {
    onVisibility = (): void => {
      if (document.visibilityState === "hidden") flushAll();
    };
    document.addEventListener("visibilitychange", onVisibility);
  }
  if (typeof window !== "undefined") {
    onPageHide = flushAll;
    window.addEventListener("pagehide", onPageHide);
  }
}

/** Test-only: tear down all listeners + queues so each test starts clean. */
export function __resetRum(): void {
  if (removeErrorHandlers) {
    removeErrorHandlers();
    removeErrorHandlers = null;
  }
  if (onVisibility && typeof document !== "undefined") {
    document.removeEventListener("visibilitychange", onVisibility);
    onVisibility = null;
  }
  if (onPageHide && typeof window !== "undefined") {
    window.removeEventListener("pagehide", onPageHide);
    onPageHide = null;
  }
  setPusherHealthHandler(null);
  setErrorFlushScheduler(null);
  if (errorFlushTimer !== null) {
    clearTimeout(errorFlushTimer);
    errorFlushTimer = null;
  }
  metricsSuppliers = [];
  vitalsQueue = [];
  initialized = false;
}
